import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Badge,
  Separator,
} from "@repo/ui";
import { format } from "date-fns";
import { ExternalLink, Package } from "lucide-react";
import type { ActivityTransaction } from "@/lib/types/activity";
import { useNavigate } from "@tanstack/react-router";

interface ActivityDetailModalProps {
  transaction: ActivityTransaction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ActivityDetailModal({
  transaction,
  open,
  onOpenChange,
}: ActivityDetailModalProps) {
  const navigate = useNavigate();

  if (!transaction) return null;

  const typeColorMap = {
    receipt: "bg-surface font-semibold text-emerald-600 border-border",
    dispense: "bg-surface font-semibold text-primary border-border",
    adjustment: "bg-surface font-semibold text-amber-600 border-border",
  };

  const handleViewItem = () => {
    navigate({ to: "/inventory", search: { search: transaction.ndc } });
    onOpenChange(false);
  };

  const handleViewSource = () => {
    if (transaction.transaction_type === "receipt") {
      navigate({ to: `/receipts?id=${transaction.transaction_id}` });
    } else if (transaction.transaction_type === "dispense") {
      navigate({ to: `/dispenses?id=${transaction.transaction_id}` });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="activity-detail-modal"
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl">Transaction Details</DialogTitle>
            <Badge
              variant="outline"
              className={typeColorMap[transaction.transaction_type]}
            >
              {transaction.transaction_type}
            </Badge>
          </div>
          <DialogDescription>
            {format(
              new Date(transaction.transaction_date),
              "MMMM d, yyyy 'at' h:mm a"
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Item Information */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Item Information
            </h3>
            <div className="grid gap-3 bg-muted/50 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-sm text-muted-foreground">
                  Item Name:
                </span>
                <span className="col-span-2 font-medium">
                  {transaction.item_name}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-sm text-muted-foreground">NDC:</span>
                <span className="col-span-2 font-mono text-sm">
                  {transaction.ndc}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-sm text-muted-foreground">
                  Current Stock:
                </span>
                <span className="col-span-2">
                  <Badge variant="secondary">
                    {transaction.stock_after_transaction}
                  </Badge>
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleViewItem}
                className="mt-2"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View in Inventory
              </Button>
            </div>
          </div>

          <Separator />

          {/* Transaction Details */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Transaction Details</h3>
            <div className="grid gap-3 bg-muted/50 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-sm text-muted-foreground">
                  Quantity Change:
                </span>
                <span
                  className={`col-span-2 font-semibold ${
                    transaction.quantity > 0
                      ? "text-emerald-600"
                      : "text-primary"
                  }`}
                >
                  {transaction.quantity > 0 ? "+" : ""}
                  {transaction.quantity}
                </span>
              </div>
              {transaction.created_by_name && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-sm text-muted-foreground">
                    Created By:
                  </span>
                  <span className="col-span-2">
                    {transaction.created_by_name}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Type-Specific Details */}
          {transaction.transaction_type === "receipt" && (
            <>
              <Separator />
              <div>
                <h3 className="text-sm font-semibold mb-3">Receipt Details</h3>
                <div className="grid gap-3 bg-muted/50 rounded-lg p-4">
                  {transaction.details.wholesaler && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        Wholesaler:
                      </span>
                      <span className="col-span-2">
                        {transaction.details.wholesaler}
                      </span>
                    </div>
                  )}
                  {transaction.details.invoice_number && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        Invoice #:
                      </span>
                      <span className="col-span-2 font-mono">
                        {transaction.details.invoice_number}
                      </span>
                    </div>
                  )}
                  {transaction.details.lot_number && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        Lot #:
                      </span>
                      <span className="col-span-2 font-mono">
                        {transaction.details.lot_number}
                      </span>
                    </div>
                  )}
                  {transaction.details.expiration_date && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        Expiration:
                      </span>
                      <span className="col-span-2">
                        {transaction.details.expiration_date}
                      </span>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleViewSource}
                    className="mt-2"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Full Receipt
                  </Button>
                </div>
              </div>
            </>
          )}

          {transaction.transaction_type === "dispense" && (
            <>
              <Separator />
              <div>
                <h3 className="text-sm font-semibold mb-3">Dispense Details</h3>
                <div className="grid gap-3 bg-muted/50 rounded-lg p-4">
                  {transaction.details.rx_number && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        RX #:
                      </span>
                      <span className="col-span-2 font-mono">
                        {transaction.details.rx_number}
                      </span>
                    </div>
                  )}
                  {transaction.details.is_duplicate_rx && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        Duplicate RX:
                      </span>
                      <Badge variant="secondary" className="w-fit">
                        Yes
                      </Badge>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleViewSource}
                    className="mt-2"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Full Dispense
                  </Button>
                </div>
              </div>
            </>
          )}

          {transaction.transaction_type === "adjustment" && (
            <>
              <Separator />
              <div>
                <h3 className="text-sm font-semibold mb-3">
                  Adjustment Details
                </h3>
                <div className="grid gap-3 bg-muted/50 rounded-lg p-4">
                  {transaction.details.reason && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        Reason:
                      </span>
                      <span className="col-span-2">
                        {{
                          physical_count: "Physical Count",
                          breakage: "Breakage/Damage",
                          expiration: "Expiration",
                          theft_loss: "Theft/Loss",
                          data_entry_error: "Data Entry Error",
                          other: "Other",
                        }[transaction.details.reason] ||
                          transaction.details.reason}
                      </span>
                    </div>
                  )}
                  {transaction.details.justification && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        Justification:
                      </span>
                      <span className="col-span-2 text-sm">
                        {transaction.details.justification}
                      </span>
                    </div>
                  )}
                  {transaction.details.expected_stock !== undefined && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        Expected Stock:
                      </span>
                      <span className="col-span-2">
                        {transaction.details.expected_stock}
                      </span>
                    </div>
                  )}
                  {transaction.details.actual_stock !== undefined && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        Actual Stock:
                      </span>
                      <span className="col-span-2">
                        {transaction.details.actual_stock}
                      </span>
                    </div>
                  )}
                  {transaction.details.password_confirmed !== undefined && (
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm text-muted-foreground">
                        Password Confirmed:
                      </span>
                      <Badge
                        variant={
                          transaction.details.password_confirmed
                            ? "default"
                            : "secondary"
                        }
                      >
                        {transaction.details.password_confirmed ? "Yes" : "No"}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
